import { Box, Collapse, Table, TableBody, TableCell, TableHead, TableRow, Button, Tooltip, Grid } from "@mui/material";
import React, { useState } from "react";
import NoRecordFoundComponent from "../Shared/NoRecords";
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { FileUploadServiceInternal } from "../../services/fileUpload.service";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import LoginIcon from '@mui/icons-material/Login';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import FileUploadModel from "../Shared/FileUploadModel";
import { toast } from 'react-toastify';
import { handleJoinMeeting, isMeetingButtonEnabled, convertToLocalTime, convertToDSTLocalTimeString } from "../Shared/Share";
import ConfirmationModal from "../Shared/ConfirmationModal";
import { AppointmentServiceInternal } from "../../services/appointment.service";
import AppointmentDetailsModal from "../Shared/AppointmentDetailsModal";
import { AppointmentDetailsDto } from "../../models/appointmentDetailsDto";
import { useNavigate } from "react-router-dom";

interface PatientData {
  appointmentid: number;
  providerName: string;
  inmatename: string;
  provider: string;
  appointmenttypeid: number;
  type: string;
  status: string;
  inmatedob: Date;
  medications: string;
  additionalinfo: string;
  admincountyid: number;
  countyid: number;
  countyname: string;
  appointmentTypeName: string;
  appointmentdatetime: any;
  createdby: number;
  providerId: number;
  athenaAppointmentId: number;
  athenaPatientId: number;
  hasAthenaAssessment: boolean;
  inmateFirstName: string;
  inmateLastName: string;
  zipCode: string;
  correctionalFacilityId: number;
}

interface PatientTableProps {
  data: PatientData[];
  setRefresh: any;
  setIsLoading: any;
}

const PatientTable = ({ data, setRefresh, setIsLoading }: PatientTableProps) => {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [open, setOpen] = React.useState(false);
  const [userId, setUserId] = useState<number>();
  const [appointmentId, setAppointmentId] = useState<number>(0);
  const [showCancelConfirmation, setCancelConfimation] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<PatientData | null>(null);
  const [openAppointmentDetails, setOpenAppointmentDetails] = React.useState(false);
  const [appointmentDetailsDto, setAppointmentDetailsDto] = useState<AppointmentDetailsDto | null>(null);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = (userId: number, appointmentId: number) => {
    setOpen(true);
    setUserId(userId);
    setAppointmentId(appointmentId);
  }

  const navigate = useNavigate();
  const handleOpenRescheduleModal = (data: any) => {
    navigate("/new-appointment", {
      state: {
        currentAppointment: data,
        pageNumber: 1,
        isReschedule: true
      }
    });
  }

  const handleCloseAppointmentDetails = () => {
    setOpenAppointmentDetails(false);
    setAppointmentDetailsDto(null);
  };

  const handleOpenAppointmentDetails = (data: any) => {
    const details: AppointmentDetailsDto = {
      athenaAppointmentId: data.athenaAppointmentId,
      inmateFirstName: data.inmateFirstName,
      inmateLastName: data.inmateLastName,
      inmatedob: data.inmatedob,
      heldforanothercounty: data.heldforanothercounty,
      countyid: data.countyid,
      countyname: data.countyname,
      medications: data.medications,
      additionalinfo: data.additionalinfo
    }
    setOpenAppointmentDetails(true);
    setAppointmentId(data.appointmentid);
    setAppointmentDetailsDto(details);
  }

  const handleExpandRow = (rowId: number) => {
    if (expandedRow === rowId) {
      setExpandedRow(null);
    } else {
      setExpandedRow(rowId);
    }
  };

  const appointmentdatetime = (date: any) => {
    const originalDate = new Date(date);
    const formattedDate = `${(originalDate.getMonth() + 1).toString().padStart(2, '0')}/${originalDate.getDate().toString().padStart(2, '0')}/${originalDate.getFullYear()}`;
    const formattedTime = `${originalDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    return {
      date: formattedDate,
      time: formattedTime
    };
  };

  const handleFileUpload = async (file: File, isRxUpload: boolean) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('userid', Number(userId).toString());
    formData.append('appointmentid', Number(appointmentId).toString());
    formData.append('isRxUpload', isRxUpload ? 'true' : 'false');
    const response = await FileUploadServiceInternal.uploadFile(formData);
    if (response?.data.isSuccess) {
      setIsLoading(false);
      toast.success(response?.data.message);
      handleClose();
    }
    else {
      setIsLoading(false);
      toast.error(response?.data.message);
    }
  };

  const handleModifyAppointmentDetails = async (
    appointmentDetails: AppointmentDetailsDto
  ) => {
    setIsLoading(true);

    try {
      const response =
        await AppointmentServiceInternal.modifyAppointmentDetails(
          appointmentId,
          appointmentDetails
        );

      if (response?.data.isSuccess) {
        toast.success(response.data.message);
        handleCloseAppointmentDetails();
        setRefresh(true);
      } else {
        toast.error(
          response?.data.message ?? "Unable to update inmate information."
        );
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ??
          "Unable to update inmate information."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelOpen = async (patientData: PatientData) => {
    setSelectedRow(patientData);
    setCancelConfimation(true);
  };

  const handleCancelClose = () => {
    setCancelConfimation(false);
  };

  const cancelAppointment = async () => {
    setIsLoading(true);
    const response = await AppointmentServiceInternal.deleteAppointment(selectedRow?.appointmentid ?? 0);
    if (response?.data.isSuccess) {
      toast.success(response?.data.message);
      setRefresh(true);
    }
    else {
      toast.error(response?.data.message);
    }
    setIsLoading(false);
    setCancelConfimation(false);
  };

  return (
    <React.Fragment>
      <Table>
        <TableHead>
          <TableRow
            style={{
              color: "#A2A8BA",
              backgroundColor: "#F6F7FB",
            }}
          >
            <TableCell className="first-cell">Inmate Name</TableCell>
            <TableCell>Provider</TableCell>
            <TableCell>Appointment Time</TableCell>
            <TableCell>Type</TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell className="text-center last-cell"></TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ height: 16, p: 0, borderBottom: "none", borderTop: "none" }} />
          </TableRow>
        </TableHead>
        {data.length > 0 ? (
          <TableBody>
            {data.map((row: PatientData, index) => (
              <React.Fragment key={`patient-table-${index}`}>
                <TableRow
                  sx={{
                    borderRadius: "20px",
                    backgroundColor: "#FFFFFF",
                    borderWidth: "0",
                  }}
                >
                  <TableCell className="first-cell">
                    <span className="font-semibold">
                      {row.inmatename === "" ? (row.inmateFirstName + " " + row.inmateLastName) : row.inmatename}
                    </span>
                    <div className="quick-view-value">
                      <Button
                        onClick={() => handleExpandRow(row.appointmentid)}
                        aria-label="expand row"
                        startIcon={<EventAvailableIcon fontSize="small" />}
                        sx={{
                          '&:hover': { backgroundColor: 'transparent' },
                          pl: 0
                        }}
                      >
                        Quick View
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className="quick-view-label">{row.providerName}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className="quick-view-label">
                        <strong>{appointmentdatetime(row.appointmentdatetime).date} &nbsp;&nbsp;&nbsp;&nbsp;{convertToLocalTime(new Date(row.appointmentdatetime).toTimeString())}</strong>
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className="quick-view-label">
                        {row.appointmentTypeName}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Tooltip title="Attach/Remove Files" placement="top">
                        <Button
                          variant="contained"
                          style={{ background: '#54bdbe' }}
                          onClick={() => handleOpen(row.createdby, row.appointmentid)}
                          startIcon={<AttachFileIcon fontSize="small" style={{ transform: 'rotate(222deg)' }} />}
                        >
                          Files
                        </Button>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={() => handleOpenRescheduleModal(row)}
                        startIcon={<EditCalendarIcon fontSize="small" />}
                      >
                        Reschedule
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleCancelOpen(row)}
                        startIcon={<EventBusyIcon fontSize="small" />}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell className="last-cell">
                    <Button
                      variant="contained"
                      onClick={() => handleJoinMeeting(row.providerId)}
                      disabled={!isMeetingButtonEnabled((new Date(row.appointmentdatetime)))}
                      startIcon={<LoginIcon fontSize="small" />}
                    >
                      Join
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    className="p-0 bottom-radius"
                    style={{
                      background: "#F6F7FB",
                      border: "0px solid #E7E5E4",
                    }}
                    colSpan={7}
                  >
                    <Collapse
                      style={{ padding: "15px" }}
                      in={expandedRow === row.appointmentid}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Grid container spacing={2} className="text-sm" justifyContent="space-between">
                        {renderCollapseRows(row)}
                        <Grid
                          item
                          xs={12}
                          sm={2}
                        >
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Tooltip title="Update Patient Information" placement="bottom">
                                <Button
                                  variant="outlined"
                                  style={{ color: '#54bdbe' }}
                                  onClick={() => handleOpenAppointmentDetails(row)}
                                  startIcon={<EditNoteOutlinedIcon fontSize="small" />}
                                >
                                  Edit Info
                                </Button>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Collapse>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ height: 16, p: 0, borderBottom: "none", borderTop: "none" }} />
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        ) : (
          <TableBody>
            <NoRecordFoundComponent cellSpan={7} />
          </TableBody>
        )}
      </Table>
      <FileUploadModel
        open={open}
        onClose={handleClose}
        handleFileUpload={handleFileUpload}
        appointmentId={appointmentId}
      />
      <ConfirmationModal
        open={showCancelConfirmation}
        onClose={handleCancelClose}
        onConfirm={cancelAppointment}
        title={"Cancel Appointment?"}
        message={(selectedRow?.inmatename === "" ? selectedRow?.inmateFirstName + " " + selectedRow?.inmateLastName : selectedRow?.inmatename) + ", " + appointmentdatetime(selectedRow?.appointmentdatetime).date + " @ " + convertToDSTLocalTimeString(selectedRow?.appointmentdatetime.toString()) + ", " + selectedRow?.appointmentTypeName}
        confirmText={"Yes"}
        cancelText={"No"}
      />
      <AppointmentDetailsModal
        open={openAppointmentDetails}
        onClose={handleCloseAppointmentDetails}
        handleModifyDetails={handleModifyAppointmentDetails}
        appointmentDetails={appointmentDetailsDto!}
      />
    </React.Fragment >
  );
}

const formatDate = (date: Date | string): string => {
  const parsedDate = new Date(date);
  return parsedDate instanceof Date && !isNaN(parsedDate.getTime())
    ? `${(parsedDate.getMonth() + 1).toString().padStart(2, '0')}/${parsedDate
      .getDate()
      .toString()
      .padStart(2, '0')}/${parsedDate.getFullYear()}`
    : 'Invalid Date';
}

export default PatientTable;

const renderCollapseRows = (rowData: PatientData) => {
  const collapseData = [
    { label: "Current Medications:", value: rowData.medications },
    { label: "Additional Information:", value: rowData.additionalinfo },
    { label: "Date of Birth:", value: formatDate(rowData.inmatedob) },
    { label: "County Held For:", value: rowData.countyname },
  ];

  const collapseRows = collapseData.map((item, index) => (
    <Grid
      item
      xs={12}
      sm={2}
      key={`quick-view-${rowData.athenaAppointmentId}-${index}`}
    >
      <Grid container spacing={1}>
        <Grid item xs={12}>
          {item.label}
        </Grid>
        <Grid item xs={12} sx={{ color: 'text.secondary' }}>
          {item.value}
        </Grid>
      </Grid>
    </Grid>
  ));

  return collapseRows;
}